import { useMemo } from 'preact/hooks';

import { useSystemStatusSnapshot } from '../utils/hooks/use-system-status-polling';

/**
 * Custom hook to check system status. Reads from the shared status poll (see
 * system-status-store), so it costs no extra requests however many components use it.
 *
 * `status` is null while loading, when the system has no status record, or when the status
 * could never be loaded - callers treat null as offline.
 *
 * @param {string} systemKey - The system identifier (e.g., 'rdarr', 'ewis')
 * @returns {{ status: Object|null, isLoading: boolean, error: Error|null }}
 */
export const useSystemStatus = (systemKey) => {
    const { systems, isLoaded, error } = useSystemStatusSnapshot();

    // Reference-stable per system: the store keeps the same object for a system whose status
    // hasn't changed, so consumers don't re-run effects because a different system changed.
    const status = useMemo(
        () => (systemKey ? (systems?.[systemKey.toLowerCase()] ?? null) : null),
        [systems, systemKey]
    );

    return { status, isLoading: Boolean(systemKey) && !isLoaded, error };
};
